import { useCallback, useState } from 'react';

type TInstallment = { id: number; number: number; value: number; key: string };

function getSelectedTotal(installments: TInstallment[], selected: number[]) {
  return installments.reduce((total, installment) => {
    if (!selected.includes(installment.id)) return total;

    return total + installment.value;
  }, 0);
}

function getNewSelectedPendenciesState(
  clickedPendency: number,
  selected: number[],
  installments: TInstallment[],
) {
  const clickedInstallment = installments.find(
    installment => installment.id === clickedPendency,
  );

  if (!clickedInstallment) return selected;

  if (selected.includes(clickedInstallment.id)) {
    return handleDeselectPendency(clickedInstallment, selected, installments);
  }

  return handleSelectPendency(clickedInstallment, selected, installments);
}

function handleDeselectPendency(
  clickedInstallment: TInstallment,
  selected: number[],
  installments: TInstallment[],
) {
  const installmentsToRemove = installments
    .filter(
      installment =>
        installment.key === clickedInstallment.key &&
        installment.number >= clickedInstallment.number,
    )
    .map(installment => installment.id);

  const newSelected = selected.filter(id => !installmentsToRemove.includes(id));

  return newSelected;
}

function handleSelectPendency(
  clickedInstallment: TInstallment,
  selected: number[],
  installments: TInstallment[],
) {
  const installmentsToSelect = installments
    .filter(
      installment =>
        installment.key === clickedInstallment.key &&
        installment.number <= clickedInstallment.number,
    )
    .map(installment => installment.id);

  const newSelectedInstallments = new Set([
    ...selected,
    ...installmentsToSelect,
  ]);

  return [...newSelectedInstallments.values()];
}

type TParams = {
  installments: TInstallment[];
};

export default function usePendingIssuesSelection({ installments }: TParams) {
  const [selectedPendencies, setSelectedPendencies] = useState<number[]>([]);

  const handleClickPendency = useCallback(
    (id: number) => () => {
      setSelectedPendencies(selectedPendencies =>
        getNewSelectedPendenciesState(id, selectedPendencies, installments),
      );
    },
    [installments],
  );

  const total = getSelectedTotal(installments, selectedPendencies);

  return {
    total,
    selectedPendencies,
    handleClickPendency,
  };
}
